import * as React from 'react';
import { connect } from 'react-redux';
import { connectSsr } from '@bart.sk-eshop/ssr-service';
import { __ } from '@bart.sk-eshop/react-i18n';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router';
import styled from 'styled-components';
import { rem, rgba } from 'polished';
import {
  URL_FOR_SCHOOLS,
  URL_FURNITURE_SCHOOLS,
  URL_REGISTRATION_SCHOOL,
} from '../../../constants';
import API from '../../../services/API';
import { getImagePath } from '../../../utilities/product';
import MetaTags from '../../../components/MetaTags/MetaTags';
import {
  Page,
  ContentBox,
  Section,
  PillButton,
  OutlineButton,
  YellowButton,
  BadgeIcon,
  mapFooterState,
  CarouselWrap,
  CarouselTrack,
  OverlayArrow,
  MobileArrows,
  ArrowButton,
  Chevron,
  useCarousel,
  audienceColor,
  sectionHeadingBase,
  desktopHeaderButton,
  SITE_ORIGIN,
  StructuredData,
} from '../_shared';

const ASSET = '/images/school-intro';

/* ------------------------------------------------------------------ *
 * Section 1 — Hero: furniture card + 4 feature badges
 * ------------------------------------------------------------------ */

const Hero = styled(Section)`
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};
  ${({ theme }) => theme.media('md')} {
    gap: ${rem(40)};
  }
`;

const HeroCard = styled.div`
  position: relative;
  border-radius: ${rem(16)};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: ${audienceColor('pinkHero')};
  ${({ theme }) => theme.media('md')} {
    border-radius: ${rem(24)};
    /* Taller than the text block so the panel can sit in the upper portion
       (top-aligned, like Figma) instead of dead-centre. */
    min-height: ${rem(420)};
    justify-content: flex-start;
  }
`;

const HeroPhotoMobile = styled.div`
  width: 100%;
  aspect-ratio: 3 / 2;
  /* hero-mobile.webp is pre-cropped to the colourful wall + desks (the light
     ceiling is removed) and is already 3:2, so it fills the box with no crop
     and never reveals a light "gap" at the top. */
  background: ${({ theme }) =>
      `linear-gradient(180deg, ${rgba(theme.color.audience.pinkHero, 0)} 90%, ${
        theme.color.audience.pinkHero
      } 100%)`},
    url('${ASSET}/hero-mobile.webp') center / cover no-repeat;
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const HeroPhotoDesktop = styled.div`
  display: none;
  ${({ theme }) => theme.media('md')} {
    display: block;
    position: absolute;
    inset: 0;
    background: url('${ASSET}/hero-furniture-desktop.webp') right center / cover
      no-repeat;
  }
`;

const HeroPanel = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
  ${({ theme }) => theme.media('md')} {
    /* Wide enough for the intro paragraph to wrap to two lines like Figma. */
    max-width: ${rem(590)};
    gap: ${rem(32)};
    padding: ${rem(60)};
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  ${({ theme }) => theme.media('md')} {
    gap: ${rem(24)};
  }
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-weight: 700;
  /* Figma is 42px on a 486px-wide frame; scaled down so the two-line break
     ("Nábytok" / "a vybavenie škôl") still holds on real phone widths. */
  font-size: ${rem(34)};
  line-height: 1.24;
  color: ${audienceColor('red')};
  ${({ theme }) => theme.media('md')} {
    font-size: ${rem(42)};
  }
`;

const HeroTitleAccent = styled.span`
  color: ${audienceColor('maroon')};
`;

const HeroText = styled.p`
  margin: 0;
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: 1.5;
  color: ${audienceColor('maroon')};
`;

const Badges = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: ${rem(16)};
  row-gap: ${rem(32)};
  padding: 0 ${rem(16)} ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    grid-template-columns: repeat(4, 1fr);
    gap: ${rem(48)};
    padding: ${rem(8)} 0 ${rem(20)};
  }
`;

const Badge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};
  text-align: center;
`;

const HeroBadgeIcon = styled(BadgeIcon)`
  width: ${rem(44)};
  height: ${rem(44)};
  box-shadow: 0 ${rem(2)} ${rem(8)} rgba(0, 0, 0, 0.11);
  ${({ theme }) => theme.media('md')} {
    width: ${rem(57)};
    height: ${rem(57)};
  }
`;

const BadgeText = styled.span`
  font-size: ${rem(18)};
  font-weight: 700;
  line-height: 1.44;
  color: ${audienceColor('maroon')};
  max-width: ${rem(250)};

  ${({ theme }) => theme.media('md')} {
    font-size: ${rem(21)};
    line-height: 1.38;
  }
`;

interface FeatureBadge {
  icon: string;
  text: string;
}

const FEATURE_BADGES: FeatureBadge[] = [
  { icon: 'badge-eso.svg', text: __('Vlastná značka nábytku ESO') },
  { icon: 'badge-slovensko.svg', text: __('Výroba na Slovensku') },
  {
    icon: 'badge-zaruka.svg',
    text: __('Doživotná záruka na kovovú konštrukciu'),
  },
  { icon: 'badge-doprava.svg', text: __('Doprava zdarma v rámci celej SR') },
];

const HeroSection = () => (
  <Hero>
    <HeroCard>
      <HeroPhotoDesktop aria-hidden="true" />
      <HeroPhotoMobile role="img" aria-label={__('Nábytok a vybavenie škôl')} />
      <HeroPanel>
        <HeroTitle>
          {__('Nábytok')}
          <br />
          <HeroTitleAccent>{__('a vybavenie škôl')}</HeroTitleAccent>
        </HeroTitle>
        <HeroCopy>
          <HeroText>
            {__(
              'Už vyše 15 rokov navrhujeme, vyrábame a dodávame nábytok a vybavenie do škôl - najlepšia cena, overená kvalita.',
            )}
          </HeroText>
          <PillButton to={URL_FURNITURE_SCHOOLS}>
            {__('Ponuka nábytku a vybavenia')}
          </PillButton>
        </HeroCopy>
      </HeroPanel>
    </HeroCard>
    <Badges>
      {FEATURE_BADGES.map((b) => (
        <Badge key={b.icon}>
          <HeroBadgeIcon src={`${ASSET}/${b.icon}`} />
          <BadgeText>{b.text}</BadgeText>
        </Badge>
      ))}
    </Badges>
  </Hero>
);

/* ------------------------------------------------------------------ *
 * Section 2 — Skvelko: intro card + two benefit cards + CTA
 * ------------------------------------------------------------------ */

const Skvelko = styled(Section)`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`;

const SkvelkoIntro = styled.div`
  overflow: hidden;
  background: ${audienceColor('creamYellow')};
  border-radius: ${rem(16)};
  padding: ${rem(20)} ${rem(20)} 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: stretch;
    gap: ${rem(32)};
    padding: ${rem(48)} ${rem(48)} 0;
    overflow: visible;
  }
`;

const SkvelkoIntroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    flex: 1 1 auto;
    justify-content: flex-start;
  }
`;

const SkvelkoHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(16)};
  }
`;

const SkvelkoLogo = styled.img`
  width: ${rem(150)};
  height: auto;
  margin-bottom: ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    margin-bottom: ${rem(0)};
  }
`;

const SkvelkoHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-weight: 700;
  font-size: ${rem(22)};
  line-height: 1.36;
  color: ${audienceColor('dark')};
  ${({ theme }) => theme.media('md')} {
    max-width: ${rem(330)};
  }
`;

const SkvelkoText = styled.p`
  margin: 0;
  font-size: ${rem(18)};
  font-weight: 400;
  line-height: 1.44;
  color: ${audienceColor('dark')};
  z-index: 2;

  ${({ theme }) => theme.media('md')} {
    max-width: ${rem(500)};
    margin-bottom: ${rem(32)};
  }

  b {
    font-weight: 700;
  }
`;

const SkvelkoKid = styled.img`
  display: block;
  width: 100%;
  max-width: ${rem(300)};
  height: auto;
  align-self: center;
  ${({ theme }) => theme.media('md')} {
    width: ${rem(450)};
    max-width: none;
    /* Full height on the right with no top offset (Figma): stretch to the card
       height and bleed over its top padding (SkvelkoIntro's overflow:hidden
       clips the rounded top). Bottom-anchored cover keeps the kid in frame. */
    align-self: stretch;
    height: auto;
    object-fit: cover;
    object-position: bottom center;
    margin-top: ${rem(-58)};
    overflow: visible;
    margin-right: ${rem(20)};
  }
`;

const BenefitCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    gap: ${rem(24)};
    align-items: stretch;
    & > * {
      flex: 1 1 0;
    }
  }
`;

const BenefitCard = styled.div`
  background: ${audienceColor('cream')};
  border-radius: ${rem(16)};
  box-shadow: 0 ${rem(4)} ${rem(6)} rgba(0, 0, 0, 0.04);
  padding: ${rem(24)} ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`;

const BenefitHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding-bottom: ${rem(24)};
  border-bottom: ${rem(1)} solid rgba(0, 0, 0, 0.2);
`;

const BenefitIcon = styled(BadgeIcon)`
  width: ${rem(40)};
  height: ${rem(40)};
  background: ${({ theme }) => theme.color.secondary};
  border-radius: 50%;
`;

const BenefitTitle = styled.h3`
  margin: 0;
  font-size: ${rem(21)};
  font-weight: 700;
  line-height: 1.38;
  color: ${audienceColor('dark')};
`;

const BenefitList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
`;

const BenefitItem = styled.li`
  display: flex;
  gap: ${rem(12)};
`;

const CheckIcon = styled.img`
  width: ${rem(24)};
  height: ${rem(24)};
  flex: 0 0 auto;
  margin-top: ${rem(4)};
`;

const BenefitItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`;

const BenefitItemBold = styled.span`
  font-size: ${rem(16)};
  font-weight: 700;
  line-height: 1.5;
  color: ${audienceColor('dark')};
`;

const BenefitItemReg = styled.span`
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: 1.5;
  color: ${audienceColor('grayDark')};
`;

const SkvelkoButton = styled(YellowButton)`
  ${({ theme }) => theme.media('md')} {
    align-self: center;
  }
`;

interface Benefit {
  text: string;
  sub: string;
}

const SCHOOL_BENEFITS: Benefit[] = [
  {
    text: __('Jednotné pomôcky pre všetkých žiakov'),
    sub: __('Každý žiak má presne to, čo učiteľ požaduje'),
  },
  {
    text: __('Učiteľ má plnú kontrolu nad zoznamom'),
    sub: __('Zoznam si nastaví podľa svojich potrieb'),
  },
  {
    text: __('Menej administratívy na začiatku roka'),
    sub: __('Odpadá rozdávanie a kontrola papierových zoznamov'),
  },
  {
    text: __('Odmena 10 % vo vernostných bodoch'),
    sub: __('pre vašu školu za každý zakúpený balíček'),
  },
];

const PARENT_BENEFITS: Benefit[] = [
  {
    text: __('Všetko na jednom mieste'),
    sub: __('Pripravený kompletný zoznam bez hľadania pomôcok v obchodoch'),
  },
  {
    text: __('Rýchly nákup bez stresu'),
    sub: __('Objednávka z domu na pár klikov'),
  },
  {
    text: __('Presne podľa požiadaviek školy'),
    sub: __('Istota správnych pomôcok pre dieťa'),
  },
];

const BenefitColumn = ({
  icon,
  title,
  benefits,
}: {
  icon: string;
  title: React.ReactNode;
  benefits: Benefit[];
}) => (
  <BenefitCard>
    <BenefitHeader>
      <BenefitIcon src={`${ASSET}/${icon}`} />
      <BenefitTitle>{title}</BenefitTitle>
    </BenefitHeader>
    <BenefitList>
      {benefits.map((b) => (
        <BenefitItem key={b.text}>
          <CheckIcon
            src={`${ASSET}/skvelko-check.svg`}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
          />
          <BenefitItemText>
            <BenefitItemBold>{b.text}</BenefitItemBold>
            <BenefitItemReg>{b.sub}</BenefitItemReg>
          </BenefitItemText>
        </BenefitItem>
      ))}
    </BenefitList>
  </BenefitCard>
);

const SkvelkoSection = () => (
  <Skvelko>
    <SkvelkoIntro>
      <SkvelkoIntroText>
        <SkvelkoHead>
          <SkvelkoLogo
            src="/images/skvelko-logo.svg"
            alt="Skvelko"
            loading="lazy"
            decoding="async"
          />
          <SkvelkoHeading>{__('Zoznamy školských pomôcok')}</SkvelkoHeading>
        </SkvelkoHead>
        <SkvelkoText>
          <b>{__('Služba Skvelko')}</b>{' '}
          {__(
            'je centrálne miesto na internete, kde sa nachádzajú zoznamy odporúčaných pomôcok slovenských škôl. Rodičia si ich tu môžu zakúpiť na pár klikov.',
          )}
        </SkvelkoText>
      </SkvelkoIntroText>
      <SkvelkoKid
        src={`${ASSET}/skvelko-kid.webp`}
        width={562}
        height={329}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
      />
    </SkvelkoIntro>

    <BenefitCards>
      <BenefitColumn
        icon="skvelko-ico-skoly.svg"
        title={__('Výhody Skvelka pre školy')}
        benefits={SCHOOL_BENEFITS}
      />
      <BenefitColumn
        icon="skvelko-ico-rodicia.svg"
        title={__('Výhody Skvelka pre rodičov')}
        benefits={PARENT_BENEFITS}
      />
    </BenefitCards>

    <SkvelkoButton to={'/pre-skolske-zariadenia/co-je-skvelko'}>
      {__('Zistiť viac o službe Skvelko')}
    </SkvelkoButton>
  </Skvelko>
);

/* ------------------------------------------------------------------ *
 * Section 3 — School registration: benefits list + two CTAs + photo
 * ------------------------------------------------------------------ */

const Registration = styled.section`
  position: relative;
  overflow: hidden;
  background: ${audienceColor('pink')};
  border-radius: ${rem(24)} ${rem(24)} 0 0;
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(24)};
  padding-bottom: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row-reverse;
    align-items: stretch;
    border-radius: ${rem(32)} ${rem(32)} 0 0;
  }
`;

const RegPhotoCol = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 ${rem(20)};
  margin-right: ${rem(-130)};
  padding-left: ${rem(84)};
  padding-top: ${rem(28)};
  ${({ theme }) => theme.media('md')} {
    flex: 0 0 50%;
    align-items: flex-end;
    justify-content: flex-end;
    margin-right: ${rem(0)};
    padding-left: ${rem(0)};
    padding-top: ${rem(0)};
  }
`;

const RegPhotoMask = styled.div`
  position: relative;
  width: 100%;
  max-width: ${rem(600)};
  aspect-ratio: 691 / 627;
  overflow: hidden;
  background: url('${ASSET}/reg-mask.webp') top center / 100% auto no-repeat;
`;

const RegPhoto = styled.img`
  position: absolute;
  top: 18.5%;
  width: 68%;
  height: auto;
`;

const RegContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};
  padding: ${rem(32)} ${rem(20)};
  ${({ theme }) => theme.media('md')} {
    flex: 0 0 50%;
    gap: ${rem(40)};
    padding: ${rem(60)} ${rem(56)};
  }
`;

const RegHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-weight: 700;
  font-size: ${rem(28)};
  line-height: 1.36;
  color: ${audienceColor('maroon')};
  max-width: ${rem(480)};
`;

const RegHeadingAccent = styled.span`
  color: ${audienceColor('red')};
`;

const RegSubtext = styled.p`
  margin: 0;
  font-size: ${rem(21)};
  font-weight: 400;
  line-height: 1.38;
  color: ${audienceColor('maroon')};
  max-width: ${rem(480)};
`;

const RegList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const RegItem = styled.li`
  display: flex;
  gap: ${rem(16)};
  padding: ${rem(16)} 0;

  & + & {
    border-top: ${rem(1)} solid rgba(0, 0, 0, 0.1);
  }
`;

const RegCheck = styled.img`
  width: ${rem(24)};
  height: ${rem(24)};
  flex: 0 0 auto;
  margin-top: ${rem(2)};
`;

const RegItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`;

const RegItemBold = styled.span`
  font-size: ${rem(16)};
  font-weight: 700;
  line-height: 1.5;
  color: ${audienceColor('dark')};

  /* The first benefit's title wraps within 330px instead of stretching full
     width (per design). */
  ${RegItem}:first-child & {
    max-width: ${rem(330)};
  }
`;

const RegItemDesc = styled.span`
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: 1.5;
  color: ${audienceColor('gray')};

  /* The second benefit's description wraps within 400px instead of stretching
     full width (per design). */
  ${RegItem}:nth-child(2) & {
    max-width: ${rem(400)};
  }
`;

const RegButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

interface RegBenefit {
  title?: string;
  desc: string;
}

const REGISTRATION_BENEFITS: RegBenefit[] = [
  {
    title: __('Výhodné veľkoobchodné ceny so zľavou až do 40%'),
    desc: __('na školské potreby, kancelárske potreby a drogériu'),
  },
  {
    desc: __(
      'Za každý rodičovský nákup balíčka získate ako škola 10 % späť vo forme vernostných bodov',
    ),
  },
  {
    title: __('K dispozícii tisíce produktov:'),
    desc: __('nábytok, školské pomôcky, kancelársky a hygienický sortiment'),
  },
  {
    title: __('Vlastná značka školského nábytku'),
    desc: __('s doživotnou zárukou na konštrukciu.'),
  },
];

const RegistrationSection = () => (
  <Registration>
    <RegPhotoCol>
      <RegPhotoMask>
        <RegPhoto
          src={`${ASSET}/reg-riaditelka-person.webp`}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
      </RegPhotoMask>
    </RegPhotoCol>
    <RegContent>
      <RegHeading>
        {__('Škola registráciou v e-shope ')}
        <RegHeadingAccent>{__('Daffer.sk')}</RegHeadingAccent>
        {__(' získava')}
      </RegHeading>
      <RegSubtext>
        {__('Výhodné ceny a všetko čo vaša škola potrebuje na jednom mieste')}
      </RegSubtext>
      <RegList>
        {REGISTRATION_BENEFITS.map((b) => (
          <RegItem key={b.desc}>
            <RegCheck
              src={`${ASSET}/reg-check.svg`}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
            />
            <RegItemText>
              {b.title ? <RegItemBold>{b.title}</RegItemBold> : null}
              <RegItemDesc>{b.desc}</RegItemDesc>
            </RegItemText>
          </RegItem>
        ))}
      </RegList>
      <RegButtons>
        <PillButton to={URL_REGISTRATION_SCHOOL}>
          {__('Chcem registrovať školu')}
        </PillButton>
        <OutlineButton
          to={`/pre-skolske-zariadenia/velkoobchodne-ceny-v-eshope`}
        >
          {__('Viac o výhodách pre školy')}
        </OutlineButton>
      </RegButtons>
    </RegContent>
  </Registration>
);

/* ------------------------------------------------------------------ *
 * Section 4 — Partner / trust: heading + photo, 3 stats, testimonials
 * ------------------------------------------------------------------ */

const Partner = styled.section`
  /* Connected to the Registration section (Figma): the cream block tucks up over
     the pink section's bottom, its rounded top overlapping it. The negative
     margin both cancels the ContentBox gap and pulls it into the overlap; the
     z-index keeps the cream on top of the pink. */
  position: relative;
  z-index: 1;
  /* Overlap the pink Registration by ~the corner radius so the rounded top sits
     over the pink (visible). Without the overlap the corners would reveal the
     white ContentBox, and cream-on-white makes them invisible.
     net overlap = ContentBox gap (40) + Registration margin-bottom (24) + this. */
  margin-top: -${rem(88)};
  background: ${audienceColor('cream')};
  border-radius: ${rem(24)} ${rem(24)} 0 0;
  display: flex;
  flex-direction: column;
  padding: ${rem(32)} ${rem(16)} ${rem(40)};
  ${({ theme }) => theme.media('md')} {
    margin-top: -${rem(96)};
    border-radius: ${rem(32)} ${rem(32)} 0 0;
    padding: ${rem(60)} ${rem(56)};
  }
`;

const PartnerHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(40)};
  }
`;

const PartnerHeading = styled.h2`
  ${sectionHeadingBase};
  color: ${audienceColor('goldHeading')};
  ${({ theme }) => theme.media('md')} {
    flex: 1 1 auto;
    max-width: ${rem(500)};
  }
`;

const PartnerKids = styled.img`
  display: block;
  width: 100%;
  height: auto;
  ${({ theme }) => theme.media('md')} {
    width: 46%;
  }
`;

const StatsCard = styled.div`
  background: ${audienceColor('creamYellow')};
  border-radius: ${rem(24)};
  padding: ${rem(24)} ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};
  margin-bottom: ${rem(32)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    gap: ${rem(24)};
    margin-left: ${rem(-32)};
    margin-right: ${rem(-32)};
    & > * {
      flex: 1 1 0;
    }
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};
  text-align: center;
`;

const StatIcon = styled(BadgeIcon)`
  width: ${rem(56)};
  height: ${rem(56)};
  background: ${({ theme }) => theme.color.secondary};
  border-radius: 50%;
`;

const StatText = styled.span`
  font-size: ${rem(21)};
  font-weight: 700;
  line-height: 1.38;
  color: ${audienceColor('dark')};
  max-width: ${rem(230)};
`;

const Testimonials = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    gap: ${rem(52)};
    margin-left: ${rem(16)};
    margin-right: ${rem(16)};
    & > * {
      flex: 1 1 0;
    }
  }
`;

const TestimonialCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: ${rem(16)};
  overflow: hidden;
  /* As flex items (flex: 1 1 0) they'd default to min-width: auto and refuse to
     shrink, overflowing the row; min-width: 0 lets them share it equally. */
  min-width: 0;
  background: ${audienceColor('yellow')};
`;

const TestimonialPhoto = styled.div`
  flex: 1 1 auto;
  /* Mobile (stacked cards): the cover fit scales the photo by the card width,
     so a fixed height would crop the bottom of the head more the wider the phone
     (and cut it at the eyes on tablets). An aspect ratio keeps the box growing
     with the width; 8/9 is tall enough to fit the whole head of the widest of
     the three headshots (507×600) with a margin above the fade. */
  aspect-ratio: 8 / 9;
  /* Fallback floor for browsers without aspect-ratio support. */
  min-height: ${rem(300)};
  background: ${({ theme }) =>
      `linear-gradient(180deg, ${rgba(theme.color.audience.yellow, 0)} 80%, ${
        theme.color.audience.yellow
      } 97%)`},
    var(--photo) top center / cover no-repeat;
  ${({ theme }) => theme.media('md')} {
    /* Desktop (3 in a row): the flex-grown box is tall enough on its own —
       don't let the mobile ratio inflate the row height. */
    aspect-ratio: auto;
    min-height: ${rem(360)};
  }
`;

const TestimonialQuote = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  background: ${audienceColor('cream')};
  border-radius: ${rem(12)};
  padding: ${rem(24)} ${rem(20)};
  /* Pull the quote a touch over the photo's yellow fade — keeps the floating
     feel of the design without covering the person's face. */
  margin: -${rem(24)} ${rem(16)} ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    margin: -${rem(24)} ${rem(32)} ${rem(32)};
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`;

const TestimonialName = styled.span`
  font-size: ${rem(21)};
  font-weight: 700;
  line-height: 1.38;
  color: ${audienceColor('dark')};
`;

const TestimonialSchool = styled.span`
  font-size: ${rem(16)};
  font-weight: 700;
  line-height: 1.5;
  color: ${audienceColor('gold')};
`;

const TestimonialText = styled.p`
  margin: 0;
  font-size: ${rem(16)};
  font-weight: 600;
  line-height: 1.5;
  color: ${audienceColor('dark')};
`;

const PARTNER_STATS = [
  { icon: 'stat-firma.svg', text: __('Slovenská firma na trhu od roku 1996') },
  { icon: 'stat-skoly.svg', text: __('Viac ako 2000 spokojných škôl') },
  { icon: 'stat-predajne.svg', text: __('Sieť predajní po celom Slovensku') },
];

const TESTIMONIALS = [
  {
    photo: 'testimonial-photo-3.webp',
    name: __('Mgr. Zuzana Galgóciová'),
    school: __('riaditeľka, ZŠ s MŠ Ružindol'),
    quote: __(
      'Vážim si ústretovosť, prístup a odborné poradenstvo. Veľa sa pýtam a vždy mi ochotne poradia. Výrobky sú výborné, komunikácia je rýchla a bezproblémová. Kedykoľvek zavolám, vždy mi zdvihnú telefón. Spätná väzba je veľmi rýchla a faktúru pošlú do pár hodín. Môžem len odporučiť.',
    ),
  },
  {
    photo: 'testimonial-photo-4.webp',
    name: __('Ing. Ľubica Kertesz'),
    school: __('riaditeľka, ZŠ Matejovie Liptovský Hrádok'),
    quote: __(
      'S Dafferom spolupracujeme už dlhé roky. Najviac si cením profesionálny prístup, priateľské jednanie, spoľahlivosť, flexibilitu a osobné stretnutia. Kvalitné spracovanie tovaru. Všetok nábytok mám z Dafferu. Skúšala som aj iných dodávateľov ale vždy som sa vrátila k Dafferu. Ochota vyhovieť požiadavkám. Na mieru robené veci. Som spokojná.',
    ),
  },
  {
    photo: 'testimonial-photo-5.webp',
    name: __('Mgr. Ivan Pavlík'),
    school: __('riaditeľ, ZŠ Bezručova Trenčín'),
    quote: __(
      'Cením si profesionalitu, rýchlosť, ústretovosť a snahu nájsť najlepšie riešenie pre potreby školy. Daffer sa odlišuje osobným prístupom, širokou ponukou a riešeniami na mieru. Ak hľadáte partnera, ktorý chce a dokáže pomôcť, Daffer je správna voľba.',
    ),
  },
];

const PartnerSection = () => (
  <Partner>
    <PartnerHeader>
      <PartnerHeading>
        {__('Sme hrdým partnerom slovenských škôl')}
      </PartnerHeading>
      <PartnerKids
        src={`${ASSET}/partner-kids.webp`}
        width={608}
        height={255}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
      />
    </PartnerHeader>

    <StatsCard>
      {PARTNER_STATS.map((s) => (
        <Stat key={s.icon}>
          <StatIcon src={`${ASSET}/${s.icon}`} />
          <StatText>{s.text}</StatText>
        </Stat>
      ))}
    </StatsCard>

    <Testimonials>
      {TESTIMONIALS.map((t, i) => (
        <TestimonialCard key={i}>
          <TestimonialPhoto
            style={{ ['--photo' as any]: `url('${ASSET}/${t.photo}')` }}
          />
          <TestimonialQuote>
            <TestimonialAuthor>
              <TestimonialName>{t.name}</TestimonialName>
              <TestimonialSchool>{t.school}</TestimonialSchool>
            </TestimonialAuthor>
            <TestimonialText>{t.quote}</TestimonialText>
          </TestimonialQuote>
        </TestimonialCard>
      ))}
    </Testimonials>
  </Partner>
);

/* ------------------------------------------------------------------ *
 * Section 5 — References: heading + horizontal card carousel + CTA
 * ------------------------------------------------------------------ */

const References = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    gap: ${rem(32)};
  }
`;

const RefHeader = styled.div`
  padding: 0 ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${rem(24)};
    padding: 0 ${rem(56)};
  }
`;

const RefTitle = styled.h2`
  ${sectionHeadingBase};
  color: ${audienceColor('red')};
`;

const RefTitleAccent = styled.span`
  color: ${audienceColor('maroon')};
`;

const RefCard = styled(Link)`
  flex: 0 0 ${rem(280)};
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: none;
    color: inherit;
  }
`;

const RefImg = styled.img`
  display: block;
  width: 100%;
  height: ${rem(320)};
  object-fit: cover;
  border-radius: ${rem(12)};
`;

const RefInfo = styled.div`
  background: ${audienceColor('pink')};
  border-radius: ${rem(12)};
  padding: ${rem(24)} ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  /* Cards stretch to the tallest in the row (flex align-items: stretch); growing
     the info box fills that extra height so every pink box is the same size. */
  flex: 1;
`;

const RefName = styled.span`
  font-size: ${rem(21)};
  font-weight: 700;
  line-height: 1.38;
  color: ${audienceColor('red')};
  /* The API "body" can be long; keep the headline to two lines. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RefSchool = styled.span`
  font-size: ${rem(16)};
  font-weight: 600;
  line-height: 1.5;
  color: ${audienceColor('dark')};
`;

const RefButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const RefHeaderButton = styled(PillButton)`
  ${desktopHeaderButton};
  ${({ theme }) => theme.media('md')} {
    align-self: center;
  }
`;

interface Reference {
  img?: string;
  type: string;
  school: string;
  url: string;
}

const REFERENCES_SITEMAP_ID = 181;

const stripHtml = (html?: string): string =>
  (html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const ReferencesSection = () => {
  const { carouselRef, canLeft, canRight, updateArrows, scroll } =
    useCarousel();
  const [references, setReferences] = React.useState<Reference[]>([]);

  React.useEffect(() => {
    let active = true;
    API.loadArticles({
      sitemapId: REFERENCES_SITEMAP_ID,
      isPublished: '1',
      limit: 8,
      offset: 0,
      sort: 'created_date',
      sortDir: 'desc',
    })
      .then((res: { articles?: any[] }) => {
        if (!active) {
          return;
        }
        const list: Reference[] = (res && res.articles ? res.articles : [])
          .filter((a: any) => !!a.url)
          .map((a: any) => ({
            img: a.image
              ? getImagePath(a.image, { width: 560, height: 640 })
              : undefined,
            type: stripHtml(a.json_content && a.json_content.body) || a.name,
            school: a.name,
            url: `/${a.url}`,
          }));
        setReferences(list);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    updateArrows();
  }, [references, updateArrows]);

  return (
    <References>
      <RefHeader>
        <RefTitle>
          {__('Pozrite si školy, ')}
          <RefTitleAccent>{__('ktoré sme zariadili')}</RefTitleAccent>
        </RefTitle>
        <RefHeaderButton
          to={`/pre-skolske-zariadenia/referencie-a-realizacie/triedy-a-skd`}
        >
          {__('Všetky referencie a realizácie')}
        </RefHeaderButton>
      </RefHeader>

      {references.length > 0 && (
        <>
          <CarouselWrap>
            <CarouselTrack ref={carouselRef} onScroll={updateArrows}>
              {references.map((r, i) => (
                <RefCard key={i} to={r.url}>
                  <RefImg
                    src={r.img}
                    alt={r.school}
                    loading="lazy"
                    decoding="async"
                  />
                  <RefInfo>
                    <RefName>{r.type}</RefName>
                    <RefSchool>{r.school}</RefSchool>
                  </RefInfo>
                </RefCard>
              ))}
            </CarouselTrack>
            <OverlayArrow
              type="button"
              className={`left${canLeft ? '' : ' hidden'}`}
              onClick={() => scroll(-1)}
              aria-label={__('Predošlé')}
            >
              <Chevron dir="left" />
            </OverlayArrow>
            <OverlayArrow
              type="button"
              className={`right${canRight ? '' : ' hidden'}`}
              onClick={() => scroll(1)}
              aria-label={__('Ďalšie')}
            >
              <Chevron dir="right" />
            </OverlayArrow>
          </CarouselWrap>
          <MobileArrows>
            <ArrowButton onClick={() => scroll(-1)} aria-label={__('Predošlé')}>
              <Chevron dir="left" />
            </ArrowButton>
            <ArrowButton onClick={() => scroll(1)} aria-label={__('Ďalšie')}>
              <Chevron dir="right" />
            </ArrowButton>
          </MobileArrows>
        </>
      )}

      <RefButtonRow>
        <PillButton
          to={`/pre-skolske-zariadenia/referencie-a-realizacie/triedy-a-skd`}
        >
          {__('Všetky referencie a realizácie')}
        </PillButton>
      </RefButtonRow>
    </References>
  );
};

interface SchoolsIntroProps {
  footerPhone?: string;
  footerMail?: string;
}

const SchoolsIntroBase = ({ footerPhone, footerMail }: SchoolsIntroProps) => (
  <Page>
    <MetaTags
      tags={{
        title: __('Pre školské zariadenia | Daffer.sk'),
        description: __(
          'Nábytok a vybavenie škôl od slovenského výrobcu, zoznamy školských pomôcok Skvelko a výhodné veľkoobchodné ceny. Partner slovenských škôl od roku 1996.',
        ),
        og_title: __('Pre školské zariadenia | Daffer.sk'),
        og_description: __(
          'Nábytok a vybavenie škôl od slovenského výrobcu, zoznamy školských pomôcok Skvelko a výhodné veľkoobchodné ceny. Partner slovenských škôl od roku 1996.',
        ),
        og_url: `${SITE_ORIGIN}${URL_FOR_SCHOOLS}`,
        og_image: `${SITE_ORIGIN}/images/school-intro/hero-furniture-desktop.png`,
        og_image_width: '2640',
        og_image_height: '924',
      }}
    />
    {/* Preload the LCP hero background (CSS backgrounds are discovered late)
        + the canonical URL (nothing else in the app renders one). */}
    <Helmet>
      <link rel="canonical" href={`${SITE_ORIGIN}${URL_FOR_SCHOOLS}`} />
      <link
        rel="preload"
        as="image"
        href={`${ASSET}/hero-mobile.webp`}
        media="(max-width: 991px)"
        {...({ fetchpriority: 'high' } as any)}
      />
      <link
        rel="preload"
        as="image"
        href={`${ASSET}/hero-furniture-desktop.webp`}
        media="(min-width: 992px)"
        {...({ fetchpriority: 'high' } as any)}
      />
    </Helmet>
    <StructuredData />
    <ContentBox>
      <HeroSection />
      <SkvelkoSection />
      <RegistrationSection />
      <PartnerSection />
      <ReferencesSection />
    </ContentBox>
  </Page>
);

const mapStateToProps = (state: any) => {
  const footer = mapFooterState(state);
  return { footerPhone: footer.phone, footerMail: footer.mail };
};

export const SchoolsIntro = connect(mapStateToProps)(
  connectSsr({ displayName: 'SchoolsIntro' })(SchoolsIntroBase),
);

export default SchoolsIntro;
